import { useMutation } from '@apollo/client';
import { DealRowActionType } from '@openx/components-deal/constants';
import { canArchiveDeal } from '@openx/components-deal/lib/utils/canArchiveDeal';
import type { DealActionResult, DealRowActionPayload } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import type { OaDealStatus } from '@openx/types/deal';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ArchiveDealDocument } from '../../../../graphql/mutation/deals/ArchiveDeal.generated';
import { useDealsAction } from '../../../../modules/deals/hooks/useDealsAction';

export const useDealsArchive = () => {
  const [archiveDeal] = useMutation(ArchiveDealDocument, {
    refetchQueries: ['GetDealsList', 'GetPackageUsedBy'],
  });
  const { performDealAction } = useDealsAction();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const archiveDeals = useCallback(
    async ({ deals }: { deals: DealRowActionPayload[] }): Promise<DealActionResult> => {
      const filteredDeals = deals.filter(deal => canArchiveDeal(deal.status as OaDealStatus));

      if (!filteredDeals.length) {
        return Promise.resolve({ fulfilledIds: [] });
      }

      setIsLoading(true);

      try {
        const { fulfilledIds } = await performDealAction({
          action: DealRowActionType.DELETE,
          actionHandler: deal =>
            archiveDeal({
              variables: {
                archiveDealId: deal.id,
              },
            }),
          deals: filteredDeals,
        });

        if (fulfilledIds.length) {
          try {
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.DEAL_LANDING] });
          } catch {
            // The landing query owns refresh errors; mutation success remains confirmed.
          }
        }

        return { fulfilledIds };
      } finally {
        setIsLoading(false);
      }
    },
    [performDealAction, archiveDeal, queryClient]
  );

  const archive = useCallback(
    (deals: DealRowActionPayload[]) => {
      return archiveDeals({ deals });
    },
    [archiveDeals]
  );

  return { archive, isLoading };
};
